import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItemField, FilterItem, FilterTextInput, Dropdown, RequiredState, PickerInput, DataColumnDef } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { ILocalizable, ILocale, isDefaultLocale, defaultLocale, commonMessages } from "infrastructure/localization";
import { useLocalizedValues, useLocalizer } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import { termDictionaryActions } from "../TermDictionary.actions";
import { ITermDictionaryState, ITermDictionarySearchQuery, IIndustryPickerSearchCriterion } from "../TermDictionary.state";

import { LanguageDisplayValue } from "./LanguageDisplayValue";

export interface ITermDictionaryFilterProps {
  modes: ILocalizable[];
  locales: ILocale[];
  industries: IIndustry[];
  localesSearchCriterion?: IPickerSearchCriterion<ILocale>;
  searchTermTranslations: (query: ITermDictionarySearchQuery) => void;
  searchIndustries: (query: IIndustryPickerSearchCriterion) => void;
  searchLocales: (query: IPickerSearchCriterion<ILocale>) => void;
}

const TermDictionaryFilter: React.FC<ITermDictionaryFilterProps> = ({
  modes,
  locales,
  industries,
  localesSearchCriterion,
  searchIndustries,
  searchLocales,
  searchTermTranslations
}) => {
  const localizedValues = useLocalizedValues(() => ({
    locale: glossaryMessages.filter("locale"),
    industry: glossaryMessages.filter("industry"),
    keyword: glossaryMessages.filter("keyword"),
    mode: glossaryMessages.filter("mode"),
    identifier: glossaryMessages.property("identifier"),
    name: commonMessages.property("localeName"),
    code: glossaryMessages.property("code"),
    description: glossaryMessages.property("description")
  }));

  const localize = useLocalizer();

  const [state, setState] = useState<{
    searchQuery: ITermDictionarySearchQuery;
    mode: ILocalizable;
    pinned: boolean;
  }>({
    searchQuery: { keyword: "" },
    mode: { messageId: glossaryMessages.filter("monolingual") },
    pinned: false
  });

  const [defaultLanguage] = useState(() => ({ ...defaultLocale, name: localize({ value: defaultLocale.name }) }));

  const onPickerSelect = <T, P extends keyof ITermDictionarySearchQuery>(rows: T[], name: P) => {
    setState(prevState => ({
      ...prevState,
      searchQuery: { ...prevState.searchQuery, [name]: rows[0], ...(name === "locale" ? { industry: undefined } : {}) }
    }));
  };

  const selectedLanguage = useMemo(() => arrayHelpers.createFrom(state.searchQuery.locale), [state.searchQuery.locale]);
  const selectedIndustry = useMemo(() => arrayHelpers.createFrom(state.searchQuery.industry), [state.searchQuery.industry]);

  const bilingual = state.mode.messageId.value === glossaryMessages.filter("bilingual").value;
  const withDefaultLanguage = bilingual === false && locales.some(isDefaultLocale) === false;

  const [languages, setLanguages] = useState(() => locales);

  useEffect(() => {
    if (withDefaultLanguage && (!localesSearchCriterion || applyPickerFilter([defaultLanguage], localesSearchCriterion, row => row.name).length)) {
      setLanguages([defaultLanguage, ...locales]);
    } else {
      setLanguages(locales);
    }
  }, [locales, withDefaultLanguage, defaultLanguage, localesSearchCriterion]);

  return (
    <FilterSection
      onSearch={() => {
        searchTermTranslations({
          ...state.searchQuery,
          bilingual
        });
      }}
      pinned={state.pinned}
      onPin={pinned => {
        setState(prevState => ({ ...prevState, pinned }));
      }}
    >
      <FilterItem labelText={localizedValues.mode} primaryFieldId="mode">
        <FilterItemField value={state.mode}>
          <Dropdown
            id="mode"
            items={modes}
            keySelector={mode => (mode as ILocalizable).messageId.value}
            displaySelector={mode => localize((mode as ILocalizable).messageId)}
            selected={[state.mode]}
            onSelectionChanged={items =>
              setState(prevState => ({
                ...prevState,
                mode: (items as ILocalizable[])[0],
                searchQuery: {
                  ...prevState.searchQuery,
                  locale: withDefaultLanguage && isDefaultLocale(prevState.searchQuery.locale) ? undefined : prevState.searchQuery.locale
                }
              }))
            }
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="language-item" labelText={localizedValues.locale} required={RequiredState.required} primaryFieldId="locale">
        <FilterItemField value={state.searchQuery.locale}>
          <PickerInput
            id="locale"
            dialogTitle={localizedValues.locale}
            selected={selectedLanguage}
            onSelectionChanged={rows => onPickerSelect(rows, "locale")}
            keySelector={row => row.localeIdentifier}
            displaySelector={row => (
              <LanguageDisplayValue
                locale={row}
                bilingual={bilingual}
                reversed={state.searchQuery.localeReversed}
                reverseLanguage={() => setState(prevState => ({ ...prevState, searchQuery: { ...prevState.searchQuery, localeReversed: !prevState.searchQuery.localeReversed } }))}
              />
            )}
            data={languages}
            searchText=""
            onSearch={(query, cols) => searchLocales({ query, searchedColumns: cols })}
          >
            <DataColumnDef<ILocale> id="identifier" title={localizedValues.identifier} valueSelector={row => row.localeIdentifier} searchable>
              {row => row.localeIdentifier}
            </DataColumnDef>
            <DataColumnDef<ILocale> id="name" title={localizedValues.name} valueSelector={row => row.name} searchable>
              {row => row.name}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem
        id="industry-item"
        labelText={localizedValues.industry}
        required={bilingual && state.searchQuery.locale && isDefaultLocale(state.searchQuery.locale.localeIdentifier) ? RequiredState.required : undefined}
        primaryFieldId="industry"
      >
        <FilterItemField value={state.searchQuery.industry}>
          <PickerInput
            id="industry"
            dialogTitle={localizedValues.industry}
            selected={selectedIndustry}
            onSelectionChanged={rows => onPickerSelect(rows, "industry")}
            keySelector={row => row.isiCv4Code}
            displaySelector={row => row.description}
            data={industries}
            searchText=""
            onSearch={(query, cols) =>
              searchIndustries({ query, searchedColumns: cols, localeIdentifier: state.searchQuery.locale ? state.searchQuery.locale.localeIdentifier : "" })
            }
          >
            <DataColumnDef<IIndustry> id="code" title={localizedValues.code} valueSelector={row => row.isiCv4Code} searchable>
              {row => row.isiCv4Code}
            </DataColumnDef>
            <DataColumnDef<IIndustry> id="description" title={localizedValues.description} valueSelector={row => row.description} searchable>
              {row => row.description}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.keyword} primaryFieldId="keyword">
        <FilterItemField value={state.searchQuery.keyword}>
          <FilterTextInput
            id="keyword"
            value={state.searchQuery.keyword}
            onValueChange={keyword => {
              setState(prevState => ({
                ...prevState,
                searchQuery: { ...prevState.searchQuery, keyword: keyword || "" }
              }));
            }}
          />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { termDictionary: ITermDictionaryState }) => ({
    modes: state.termDictionary.filter.modes,
    locales: state.termDictionary.filter.locales,
    industries: state.termDictionary.filter.industries,
    localesSearchCriterion: state.termDictionary.filter.localesSearchCriterion
  }),
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        searchIndustries: termDictionaryActions.searchIndustries,
        searchLocales: termDictionaryActions.searchLocales,
        searchTermTranslations: termDictionaryActions.searchTermTranslations
      },
      dispatch
    )
)(TermDictionaryFilter);
